import PropTypes from 'prop-types'
import { InlineMessageRenderer } from './InlineMessageRenderer'
import InlineMessageErrorBoundary from './InlineMessageErrorBoundary'
import StandaloneComponentRenderer from './StandaloneComponentRenderer'
import useInlineActions from '../../hooks/chat/useInlineActions'
import { ErrorMessage } from './ErrorMessage'
import { ChatMessageWithParts } from './ChatMessageWithParts'

export const ChatMessage = ({
  message,
  isStreaming,
  isLastMessage,
  onSentenceClick,
  assistantAnimationState,
}) => {
  // React hooks must be called before any returns
  const { handleComponentAction } = useInlineActions()

  // Check if this is a parts-based message and route to ChatMessageWithParts
  const hasPartsStructure = message.parts && Array.isArray(message.parts)

  if (hasPartsStructure) {
    return (
      <ChatMessageWithParts
        message={message}
        isStreaming={isStreaming}
        isLastMessage={isLastMessage}
        onSentenceClick={onSentenceClick}
        assistantAnimationState={assistantAnimationState}
      />
    )
  }

  // Legacy message rendering (backward compatibility)
  const isUser = message.role === 'user'
  const isNewUserMessage = isUser && isLastMessage && !isStreaming
  const isUserMessageBeingProcessed = isUser && isLastMessage && isStreaming
  const showStreamingIndicator = message.role === 'assistant' && isStreaming && isLastMessage

  const getTextClasses = () =>
    `leading-relaxed whitespace-pre-wrap ${
      message.isProgress ? 'text-sm text-gray-500 italic' : 'text-sm sm:text-base'
    }`

  const getAnimationClasses = () => {
    // Only apply animations to assistant messages
    if (message.role !== 'assistant') return ''

    // Only apply animations to the last assistant message
    if (!isLastMessage) return ''

    // Apply animations based on assistantAnimationState prop
    switch (assistantAnimationState) {
      case 'thinking':
        return 'animate-status-thinking'
      case 'processing':
        return 'animate-progress-working'
      case 'streaming':
        return 'animate-response-typing'
      case 'complete':
        return 'animate-response-complete'
      case 'idle':
      default:
        return ''
    }
  }

  const renderContent = () => {
    // Render error messages with special styling
    if (message.isError) {
      // Handle case where content might be an object with type and message properties
      const errorContent =
        typeof message.content === 'object' && message.content?.message
          ? message.content.message
          : message.content

      return (
        <ErrorMessage
          message={errorContent}
          trace={message.trace}
          type={message.isWarning ? 'warning' : 'error'}
        />
      )
    }

    // Check for standalone single component
    if (message.type === 'component' && message.component) {
      return (
        <InlineMessageErrorBoundary message={message}>
          <StandaloneComponentRenderer
            component={message.component}
            onComponentAction={handleComponentAction}
          />
        </InlineMessageErrorBoundary>
      )
    }

    // Check for standalone multiple components
    if (message.type === 'components' && message.components) {
      return (
        <InlineMessageErrorBoundary message={message}>
          <StandaloneComponentRenderer
            components={message.components}
            layout={message.layout}
            onComponentAction={handleComponentAction}
          />
        </InlineMessageErrorBoundary>
      )
    }

    // Check for response with components (message + components)
    if (message.type === 'response' && message.components && Array.isArray(message.components)) {
      return (
        <div>
          {message.message && (
            <div className={`${getTextClasses()} mb-3`}>
              {message.message}
              {showStreamingIndicator && !message.components?.length && (
                <span className="inline-block w-1 h-4 ml-1 animate-pulse bg-gradient-to-b from-gray-400 to-gray-600 rounded-full shadow-[0_0_8px_rgba(156,163,175,0.5)]" />
              )}
            </div>
          )}
          <InlineMessageErrorBoundary message={message}>
            <StandaloneComponentRenderer
              components={message.components}
              layout={message.layout}
              onComponentAction={handleComponentAction}
            />
          </InlineMessageErrorBoundary>
          {showStreamingIndicator && !message.message && (
            <div className="mt-2">
              <span className="inline-block w-1 h-4 animate-pulse bg-gradient-to-b from-gray-400 to-gray-600 rounded-full shadow-[0_0_8px_rgba(156,163,175,0.5)]" />
            </div>
          )}
        </div>
      )
    }

    // Check for messages with clickable sentences (new structure)
    if (message.hasClickableSentences && message.sentences) {
      return (
        <div className={`${getTextClasses()} flex items-center`}>
          {showStreamingIndicator && (
            <span className="inline-block w-1 h-4 mr-2 animate-pulse bg-gradient-to-b from-gray-400 to-gray-600 rounded-full shadow-[0_0_8px_rgba(156,163,175,0.5)]" />
          )}
          <div>
            {message.sentences.map((sentence, index) => (
              <span
                key={`${message.messageId}-sentence-${index}`}
                className="cursor-pointer bg-gray-700/20 px-1 py-0.5 rounded hover:bg-gray-600/30 hover:text-gray-200 transition-all duration-200"
                onClick={() =>
                  onSentenceClick?.(
                    sentence.sentenceUUID,
                    message.messageId,
                    message.conversationId
                  )
                }
                title="Click to view references"
              >
                {sentence.text}
              </span>
            ))}
          </div>
        </div>
      )
    }

    // Check for Option 2: Clickable Sentence (with sentence_id)
    if (message.type === 'clickableresponse' || message.sentence_id) {
      return (
        <div className={`${getTextClasses()} flex items-center`}>
          {showStreamingIndicator && (
            <span className="inline-block w-1 h-4 mr-2 animate-pulse bg-gradient-to-b from-gray-400 to-gray-600 rounded-full shadow-[0_0_8px_rgba(156,163,175,0.5)]" />
          )}
          <InlineMessageErrorBoundary message={message}>
            <InlineMessageRenderer
              message={message}
              onComponentAction={handleComponentAction}
              onSentenceClick={onSentenceClick}
            />
          </InlineMessageErrorBoundary>
        </div>
      )
    }

    // Check if message contains reference components (Option 1)
    // Ensure content is a string before calling includes()
    if (typeof message.content === 'string' && message.content.includes('[[')) {
      return (
        <div className={`${getTextClasses()} flex items-center`}>
          {showStreamingIndicator && (
            <span className="inline-block w-1 h-4 mr-2 animate-pulse bg-gradient-to-b from-gray-400 to-gray-600 rounded-full shadow-[0_0_8px_rgba(156,163,175,0.5)]" />
          )}
          <InlineMessageErrorBoundary message={message}>
            <InlineMessageRenderer
              message={message}
              onComponentAction={handleComponentAction}
              onSentenceClick={message.sentence_id ? onSentenceClick : undefined}
            />
          </InlineMessageErrorBoundary>
        </div>
      )
    }

    // Check for response messages with components object (Option 3)
    if (message.components && Object.keys(message.components).length > 0) {
      return (
        <div className={`${getTextClasses()} flex items-center`}>
          {showStreamingIndicator && (
            <span className="inline-block w-1 h-4 mr-2 animate-pulse bg-gradient-to-b from-gray-400 to-gray-600 rounded-full shadow-[0_0_8px_rgba(156,163,175,0.5)]" />
          )}
          <InlineMessageErrorBoundary message={message}>
            <InlineMessageRenderer message={message} onComponentAction={handleComponentAction} />
          </InlineMessageErrorBoundary>
        </div>
      )
    }

    // Fallback to plain text rendering
    // Handle case where content might be an object with type and message properties
    const displayContent =
      typeof message.content === 'object' && message.content?.message
        ? message.content.message
        : message.content

    return (
      <p className={`${getTextClasses()} flex items-center`}>
        {showStreamingIndicator && (
          <span className="inline-block w-1 h-4 mr-2 animate-pulse bg-gradient-to-b from-gray-400 to-gray-600 rounded-full shadow-[0_0_8px_rgba(156,163,175,0.5)]" />
        )}
        <span>{displayContent}</span>
      </p>
    )
  }

  return (
    <div
      className={`flex ${isUser ? 'justify-end' : 'justify-start'} ${
        isNewUserMessage
          ? 'animate-message-send'
          : isUser || !isStreaming || !isLastMessage
            ? 'animate-fade-in'
            : ''
      }`}
      data-conversation-id={message.conversationId || ''}
      data-message-id={message.messageId || ''}
    >
      <div className="max-w-[85%] sm:max-w-[75%]">
        <div
          className={`relative ${message.isError ? '' : 'px-5 py-4'} ${
            isUser
              ? 'message-bubble-user-modern'
              : message.isError
                ? 'bg-transparent border-transparent'
                : 'message-bubble-assistant-modern'
          } transition-all duration-200 hover:shadow-lg ${
            isUserMessageBeingProcessed ? 'animate-message-pulse' : getAnimationClasses()
          }`}
        >
          {renderContent()}

          {!message.isError && (
            <div
              className={`mt-2 flex items-center gap-2 text-xs ${
                isUser ? 'text-gray-400' : 'text-gray-500'
              }`}
            >
              <span className="opacity-75">
                {new Date().toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit',
                })}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

ChatMessage.propTypes = {
  message: PropTypes.shape({
    role: PropTypes.oneOf(['user', 'assistant']).isRequired,
    // Parts-based structure (new system)
    parts: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.oneOf(['response', 'error']).isRequired,
        message: PropTypes.string,
        timestamp: PropTypes.string.isRequired,
        order: PropTypes.number.isRequired,
        sentence_id: PropTypes.string, // For clickable sentences
        isComponent: PropTypes.bool,
        componentType: PropTypes.string,
        components: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
        layout: PropTypes.string,
        trace: PropTypes.string, // For error parts
        isWarning: PropTypes.bool, // For warning errors
        metadata: PropTypes.object,
      })
    ),
    message_id: PropTypes.string, // New underscore naming
    conversation_id: PropTypes.string, // New underscore naming
    // State flags (not persisted in storage)
    isProgress: PropTypes.bool,
    isError: PropTypes.bool,
    currentStatus: PropTypes.string, // Current status message (display-only)
    statusType: PropTypes.oneOf(['status', 'progress']), // Type of current status
    // Analytics metadata
    timestamp: PropTypes.string,
    lastUpdated: PropTypes.string,
    streamingDuration: PropTypes.number,
    // Legacy structure (backward compatibility)
    content: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        type: PropTypes.string,
        message: PropTypes.string,
        sentences: PropTypes.array,
      }),
    ]),
    isWarning: PropTypes.bool, // Whether this is a warning (e.g., rate limit) rather than an error
    trace: PropTypes.string, // Stack trace for error messages (admin only)
    type: PropTypes.string, // For 'clickableresponse', 'component', 'components', 'response' messages
    components: PropTypes.oneOfType([
      PropTypes.object, // Component definitions for inline components (legacy)
      PropTypes.array, // Array of components for standalone rendering
    ]),
    component: PropTypes.object, // Single component for standalone rendering
    layout: PropTypes.oneOf(['horizontal', 'vertical']), // Layout for multiple components
    sentence_id: PropTypes.string, // For fetching references in clickable sentences
    message: PropTypes.string, // Alternative content field for clickable responses or response with components
    conversationId: PropTypes.string, // UUID for conversation tracking (legacy)
    messageId: PropTypes.string, // UUID for message tracking (legacy)
    // New sentence-based structure
    hasClickableSentences: PropTypes.bool, // Whether message has clickable sentences
    sentences: PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string.isRequired,
        sentenceUUID: PropTypes.string.isRequired,
      })
    ), // Array of sentences with UUIDs
  }).isRequired,
  isStreaming: PropTypes.bool,
  isLastMessage: PropTypes.bool,
  onSentenceClick: PropTypes.func,
  assistantAnimationState: PropTypes.oneOf([
    'idle',
    'thinking',
    'processing',
    'streaming',
    'complete',
  ]),
}

ChatMessage.defaultProps = {
  isStreaming: false,
  isLastMessage: false,
  onSentenceClick: undefined,
  assistantAnimationState: 'idle',
}
